import { useEffect, useRef } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { IconTimesDefault, IconZoomOutline } from '@iwoca/icons';
import { Button, Input } from '@iwoca/orion';
import { clsx } from 'clsx';

import { MultiSelect } from '../../../../components/MultiSelect/MultiSelect';
import {
  PRICING_TYPE_LABELS,
  Seller,
  TERM_LENGTH_LABELS,
} from '../Seller.types';

type FilterOption = { value: string; label: string };

const PRICING_OPTIONS: FilterOption[] = Object.entries(PRICING_TYPE_LABELS).map(
  ([value, label]) => ({ value, label }),
);

const TERM_OPTIONS: FilterOption[] = Object.entries(TERM_LENGTH_LABELS).map(
  ([value, label]) => ({ value, label }),
);

type FilterBarProps = {
  sellers: Seller[] | null;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  selectedCategories: string[];
  setSelectedCategories: (value: string[]) => void;
  selectedPricings: string[];
  setSelectedPricings: (value: string[]) => void;
  selectedTerms: string[];
  setSelectedTerms: (value: string[]) => void;
};

export function FilterBar({
  sellers,
  searchQuery,
  setSearchQuery,
  selectedCategories,
  setSelectedCategories,
  selectedPricings,
  setSelectedPricings,
  selectedTerms,
  setSelectedTerms,
}: FilterBarProps) {
  const categoryOptions: FilterOption[] = Array.from(
    new Set((sellers || []).map((s) => s.industry)),
  )
    .sort()
    .map((cat) => ({ value: cat, label: cat }));

  const selectedCategoryOptions = categoryOptions.filter((o) =>
    selectedCategories.includes(o.value),
  );
  const selectedPricingOptions = PRICING_OPTIONS.filter((o) =>
    selectedPricings.includes(o.value),
  );
  const selectedTermOptions = TERM_OPTIONS.filter((o) =>
    selectedTerms.includes(o.value),
  );

  const prevSearchQueryRef = useRef('');
  useEffect(() => {
    if (searchQuery && !prevSearchQueryRef.current) {
      tracking.ampli.sellerDirectoryFilterClicked({ filter_name: 'search' });
    }
    prevSearchQueryRef.current = searchQuery;
  }, [searchQuery]);

  const activeChips: {
    key: string;
    label: string;
    capitalize: boolean;
    onRemove: () => void;
  }[] = [
    ...selectedCategoryOptions.map((o) => ({
      key: `category-${o.value}`,
      label: o.label,
      capitalize: true,
      onRemove: () =>
        setSelectedCategories(selectedCategories.filter((c) => c !== o.value)),
    })),
    ...selectedPricingOptions.map((o) => ({
      key: `pricing-${o.value}`,
      label: o.label,
      capitalize: false,
      onRemove: () =>
        setSelectedPricings(selectedPricings.filter((p) => p !== o.value)),
    })),
    ...selectedTermOptions.map((o) => ({
      key: `term-${o.value}`,
      label: o.label,
      capitalize: false,
      onRemove: () =>
        setSelectedTerms(selectedTerms.filter((t) => t !== o.value)),
    })),
  ];

  const clearAll = () => {
    setSelectedCategories([]);
    setSelectedPricings([]);
    setSelectedTerms([]);
  };

  return (
    <div className="mb-l flex flex-col gap-xl">
      <div className="flex flex-col gap-s">
        <div>
          <p className="m-0 text-l font-bold text-primary-10">Search</p>
          <p className="m-0 mt-2xs text-s font-reg text-structure-40">
            Find a supplier by name.
          </p>
        </div>
        <Input
          type="search"
          placeholder="e.g. Apex Building Supplies"
          iconLeft={<IconZoomOutline />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full"
        />
      </div>

      <div className="border-t border-solid border-secondary-90" />

      <div className="flex flex-col gap-s">
        <div className="flex items-baseline justify-between">
          <div>
            <p className="m-0 text-l font-bold text-primary-10">Filter</p>
            <p className="m-0 mt-2xs text-s font-reg text-structure-40">
              Narrow by industry, pricing type, or payment term.
            </p>
          </div>
          <Button
            variant="tertiary"
            size="small"
            onClick={clearAll}
            className={activeChips.length === 0 ? 'invisible' : ''}
          >
            Clear all
          </Button>
        </div>

        {activeChips.length > 0 && (
          <div className="flex flex-wrap gap-s">
            {activeChips.map((chip) => (
              <button
                key={chip.key}
                onClick={chip.onRemove}
                aria-label={`Remove ${chip.label} filter`}
                className="inline-flex items-center gap-2xs rounded-xs border border-solid border-secondary-90 bg-structure-100 px-xs py-2xs text-xs font-med text-secondary-20 hover:border-secondary-40"
              >
                <span className={clsx({ capitalize: chip.capitalize })}>
                  {chip.label}
                </span>
                <IconTimesDefault className="h-[10px] w-[10px]" />
              </button>
            ))}
          </div>
        )}

        <MultiSelect
          placeholder="Any industry"
          value={selectedCategoryOptions}
          options={categoryOptions}
          onChange={(opts) => {
            if (opts.length > selectedCategoryOptions.length) {
              tracking.ampli.sellerDirectoryFilterClicked({
                filter_name: 'industry',
              });
            }
            setSelectedCategories(opts.map((o) => o.value));
          }}
        />
        <MultiSelect
          placeholder="Any pricing"
          value={selectedPricingOptions}
          options={PRICING_OPTIONS}
          onChange={(opts) => {
            if (opts.length > selectedPricingOptions.length) {
              tracking.ampli.sellerDirectoryFilterClicked({
                filter_name: 'pricing',
              });
            }
            setSelectedPricings(opts.map((o) => o.value));
          }}
        />
        <MultiSelect
          placeholder="Any term length"
          value={selectedTermOptions}
          options={TERM_OPTIONS}
          onChange={(opts) => {
            if (opts.length > selectedTermOptions.length) {
              tracking.ampli.sellerDirectoryFilterClicked({
                filter_name: 'term',
              });
            }
            setSelectedTerms(opts.map((o) => o.value));
          }}
        />
      </div>
    </div>
  );
}
